import { VesselCardVesselSelectorProps } from "../models/vessel-card-header.models";
import { VesselCardVesselOption } from "../models/vessel-card.models";
import { ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { UIEvent, useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const VESSEL_OPTION_ROW_HEIGHT = 44;
const VESSEL_OPTION_VIEWPORT_HEIGHT = VESSEL_OPTION_ROW_HEIGHT * 6;
const VESSEL_OPTION_OVERSCAN = 3;

const formatVesselOption = (option: VesselCardVesselOption): string =>
  `${option.vesselName} · ${option.imo}`;

// Keeps only the visible fixed-height rows mounted while requesting the next 50-row page near the scroll boundary.
// Shows the selected vessel while closed and switches to live name-or-IMO search whenever the field is focused.
const VesselCardVesselSelector = ({
  autoFocus = false,
  error,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onQueryChange,
  onSelectVessel,
  options,
  query,
  selectedVessel,
  status,
}: VesselCardVesselSelectorProps) => {
  const [isOpen, setIsOpen] = useState(autoFocus);
  const [scrollTop, setScrollTop] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listboxRef = useRef<HTMLDivElement | null>(null);
  const listboxId = "vessel-card-vessel-options";
  const totalHeight = options.length * VESSEL_OPTION_ROW_HEIGHT;
  const viewportHeight = Math.min(
    VESSEL_OPTION_VIEWPORT_HEIGHT,
    Math.max(VESSEL_OPTION_ROW_HEIGHT, totalHeight),
  );
  const startIndex = Math.max(
    0,
    Math.floor(scrollTop / VESSEL_OPTION_ROW_HEIGHT) - VESSEL_OPTION_OVERSCAN,
  );
  const endIndex = Math.min(
    options.length,
    Math.ceil((scrollTop + viewportHeight) / VESSEL_OPTION_ROW_HEIGHT) + VESSEL_OPTION_OVERSCAN,
  );
  const visibleOptions = useMemo(
    () => options.slice(startIndex, endIndex),
    [endIndex, options, startIndex],
  );

  useEffect(() => {
    setScrollTop(0);
    setActiveIndex(-1);

    if (listboxRef.current) {
      listboxRef.current.scrollTop = 0;
    }
  }, [query]);

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    const nextScrollTop = element.scrollTop;
    setScrollTop(nextScrollTop);

    if (
      hasMore
      && !isLoadingMore
      && nextScrollTop + element.clientHeight >= element.scrollHeight - VESSEL_OPTION_ROW_HEIGHT * 2
    ) {
      onLoadMore();
    }
  };

  const openForSearch = () => {
    setIsOpen(true);

    if (selectedVessel && query) {
      onQueryChange("");
    }
  };

  const moveActiveOption = (direction: -1 | 1) => {
    if (options.length === 0) {
      return;
    }

    const nextIndex = Math.min(options.length - 1, Math.max(0, activeIndex + direction));
    const nextOptionTop = nextIndex * VESSEL_OPTION_ROW_HEIGHT;
    const listbox = listboxRef.current;
    setActiveIndex(nextIndex);

    if (!listbox) {
      return;
    }

    if (nextOptionTop < listbox.scrollTop) {
      listbox.scrollTop = nextOptionTop;
    } else if (nextOptionTop + VESSEL_OPTION_ROW_HEIGHT > listbox.scrollTop + listbox.clientHeight) {
      listbox.scrollTop = nextOptionTop + VESSEL_OPTION_ROW_HEIGHT - listbox.clientHeight;
    }
  };

  const selectActiveOption = () => {
    const option = options[activeIndex];

    if (option) {
      onSelectVessel(option.imo);
      setIsOpen(false);
    }
  };

  return (
    <div
      className="relative"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setIsOpen(false);
        }
      }}
      data-test="vessel-card-vessel-select"
    >
      <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-3.5 z-10 h-5 w-5 text-gray-500 dark:text-gray-300" aria-hidden="true" />
      <input
        id="vessel-card-vessel-search-input"
        type="search"
        autoFocus={autoFocus}
        aria-label="Search vessel name or IMO"
        value={isOpen ? query : selectedVessel ? formatVesselOption(selectedVessel) : query}
        onFocus={openForSearch}
        onClick={openForSearch}
        onChange={(event) => {
          setIsOpen(true);
          onQueryChange(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setIsOpen(true);
            moveActiveOption(event.key === "ArrowDown" ? 1 : -1);
          } else if (event.key === "Enter" && isOpen) {
            event.preventDefault();
            selectActiveOption();
          } else if (event.key === "Escape") {
            setIsOpen(false);
          }
        }}
        placeholder="Search vessel name or IMO"
        role="combobox"
        aria-controls={listboxId}
        aria-expanded={isOpen}
        aria-autocomplete="list"
        aria-activedescendant={isOpen && options[activeIndex] ? `vessel-card-option-${options[activeIndex].imo}` : undefined}
        data-test="vessel-card-vessel-search-input"
        className="w-full rounded-lg border border-gray-300 bg-white py-3 pl-11 pr-11 text-sm text-gray-900 outline-none placeholder:text-gray-500 focus:border-green focus:ring-2 focus:ring-green/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-400"
      />
      <ChevronDownIcon className="pointer-events-none absolute right-4 top-3.5 h-5 w-5 text-gray-500 dark:text-gray-300" aria-hidden="true" />

      {isOpen && (
        <div
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          onScroll={handleScroll}
          style={{ maxHeight: VESSEL_OPTION_VIEWPORT_HEIGHT }}
          className="absolute z-30 mt-2 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white text-gray-900 shadow-xl dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          data-test="vessel-card-vessel-options"
        >
          {options.length > 0 && (
            <div className="relative" style={{ height: totalHeight }}>
              {visibleOptions.map((option, visibleIndex) => {
                const optionIndex = startIndex + visibleIndex;
                const isSelected = selectedVessel?.imo === option.imo;

                return (
                  <button
                    id={`vessel-card-option-${option.imo}`}
                    key={option.imo}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setActiveIndex(optionIndex)}
                    onClick={() => {
                      onSelectVessel(option.imo);
                      setIsOpen(false);
                    }}
                    style={{
                      height: VESSEL_OPTION_ROW_HEIGHT,
                      transform: `translateY(${optionIndex * VESSEL_OPTION_ROW_HEIGHT}px)`,
                    }}
                    className={twMerge(
                      "absolute left-0 top-0 flex w-full items-center justify-between px-4 text-left text-sm hover:bg-primary-10 dark:hover:bg-teal-950/40",
                      (isSelected || activeIndex === optionIndex) && "bg-primary-10 text-green dark:bg-teal-950/40 dark:text-teal-200",
                    )}
                    data-test={`vessel-card-vessel-option-${option.imo}`}
                  >
                    <span className="truncate font-medium">{option.vesselName}</span>
                    <span className="ml-4 shrink-0 font-mono text-xs text-gray-500 dark:text-gray-300">{option.imo}</span>
                  </button>
                );
              })}
            </div>
          )}

          {(status === "loading" || isLoadingMore) && (
            <p className="px-4 py-3 text-center text-xs text-gray-600 dark:text-gray-300" data-test="vessel-card-vessels-loading">
              Loading vessels…
            </p>
          )}
          {status === "error" && (
            <p role="alert" className="px-4 py-3 text-xs text-red-700 dark:text-red-200" data-test="vessel-card-vessels-error">
              {error}
            </p>
          )}
          {error && status !== "error" && (
            <p role="alert" className="px-4 py-3 text-xs text-red-700 dark:text-red-200" data-test="vessel-card-vessels-page-error">
              {error}
            </p>
          )}
          {status === "success" && options.length === 0 && (
            <p className="px-4 py-3 text-center text-xs text-gray-600 dark:text-gray-300" data-test="vessel-card-vessels-empty">
              No vessels match this name or IMO.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default VesselCardVesselSelector;
